import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Package, Sparkles, Wand2 } from 'lucide-react';
import { TaskyButton, TaskyCard, TaskyInput, TaskyAlert } from '../tasky';
import { ProductSelectionModal, ProductSelectionData } from '../image-generation';
import ugcAdService, {
  UgcAdObjective,
  UgcAdPlatform,
  UgcAdProject,
  UgcAdRecommendation,
} from '../../services/api/ugcAdService';
import UgcAdImageKeysField, { UgcAdImageKeyItem } from './UgcAdImageKeysField';

/**
 * UGC動画広告プロジェクトの新規作成パネル（一覧ページ上部の常時表示フォーム）。
 *
 * song-ad の SongAdCreatePanel（v5 advisor-notes (3)）と同じ縮小方針:
 * **タイトル＋商品選択（任意）＋「作成」だけ**にする。構成タイプ・出演者・尺・追加指示・詳細設定は
 * すべて①情報登録タブ（`InfoStep`）に一本化し、ここでは編集しない（backendは title 以外すべて
 * デフォルト値で作成できる前提。フル設定を両方に置くと state が二重化するため持たせない）。
 *
 * 商品選択は既存バナー生成と同じ `ProductSelectionModal` をそのまま使う（新しいUXを発明しない）。
 *
 * 2026-08-22（UGC-01）: **広告媒体と広告目的を先に選ばせる**ようにした。
 * 利用者は「トーク中心か商品フォーカスか」「15秒か30秒か」を判断できないため、
 * 媒体と目的だけ選んでもらい、構成タイプ・尺はサーバーの推奨値を当てる。
 * 推奨理由も1行で見せて、なぜその値になったかが分かるようにする。
 */

export interface UgcAdCreatePanelProps {
  onCreated: (project: UgcAdProject) => void;
}

const UgcAdCreatePanel: React.FC<UgcAdCreatePanelProps> = ({ onCreated }) => {
  const [title, setTitle] = useState('');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [imageItems, setImageItems] = useState<UgcAdImageKeyItem[]>([]);

  const [platform, setPlatform] = useState<UgcAdPlatform | null>(null);
  const [objective, setObjective] = useState<UgcAdObjective | null>(null);
  const [recommendation, setRecommendation] = useState<UgcAdRecommendation | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 媒体・目的を続けて切り替えると応答の順序が入れ替わりうる。最後に投げた要求の番号を持っておき、
  // 追い越された古い応答は捨てる（そうしないと前の選択の推奨値が state を上書きする）
  const recommendationSeq = useRef(0);

  // 媒体・目的が変わるたびに推奨値を取り直す。未選択でも既定の推奨が返るので、
  // 何も選ばなくても次へ進める（詳細設定を触らずに作成できること・UGC-01の受け入れ条件）
  const loadRecommendation = useCallback(async () => {
    const seq = ++recommendationSeq.current;
    setIsLoadingRecommendation(true);
    try {
      const next = await ugcAdService.getRecommendations(platform ?? undefined, objective ?? undefined);
      if (seq !== recommendationSeq.current) return;
      setRecommendation(next);
    } catch {
      // 推奨が取れなくても作成自体はできる（サーバー側の既定値で作られる）
      if (seq !== recommendationSeq.current) return;
      setRecommendation(null);
    } finally {
      if (seq === recommendationSeq.current) setIsLoadingRecommendation(false);
    }
  }, [platform, objective]);

  useEffect(() => {
    void loadRecommendation();
  }, [loadRecommendation]);

  const productImageKeys = useMemo(() => imageItems.map((i) => i.key), [imageItems]);

  const resetForm = () => {
    setTitle('');
    setSelectedProductData(null);
    setImageItems([]);
    setPlatform(null);
    setObjective(null);
    setError(null);
  };

  /** 商品選択モーダルの確定時。選んだ画像をサムネイル表示用の imageItems に反映する */
  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);
    setImageItems(
      data.selectedImages.map((img) => ({
        key: img.file_path,
        url: img.url,
        label: img.title || img.file_name,
        source: 'product',
      }))
    );
  };

  const handleCreate = async () => {
    // 選択直後に押されると、選んだ媒体・目的と1つ前の推奨値が同じリクエストに混ざる
    if (isCreating || isLoadingRecommendation) return;
    setIsCreating(true);
    setError(null);
    try {
      const project = await ugcAdService.createProject({
        title: title.trim() || undefined,
        product_id: selectedProductData?.product.id,
        product_image_keys: productImageKeys.length > 0 ? productImageKeys : undefined,
        ad_platform: platform ?? undefined,
        ad_objective: objective ?? undefined,
        // 推奨値をそのまま初期値として入れる。利用者が構成タイプや尺を判断しなくてよくなる
        format_type: recommendation?.format_type,
        target_seconds: recommendation?.target_seconds,
      });
      resetForm();
      onCreated(project);
    } catch (e) {
      setError(ugcAdService.extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <TaskyCard variant="warm" padding="md" className="mb-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-dark">UGC動画広告プロジェクトを作成</h2>
        <p className="text-sm text-gray-500 mt-1">
          構成と長さはAIが決めます。
        </p>
      </div>

      <div className="space-y-5">
        {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

        {/* 媒体・目的（UGC-01）。ここを選ぶだけで構成タイプと尺が決まる */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">どこに出しますか？</label>
          <div className="flex flex-wrap gap-1.5">
            {(recommendation?.platforms ?? []).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPlatform(option.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  platform === option.value
                    ? 'border-primary bg-primary text-white font-medium'
                    : 'border-warm-border text-gray-600 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">何のための広告ですか？</label>
          <div className="flex flex-wrap gap-1.5">
            {(recommendation?.objectives ?? []).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setObjective(option.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  objective === option.value
                    ? 'border-primary bg-primary text-white font-medium'
                    : 'border-warm-border text-gray-600 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* なぜその値になったのかを1行で見せる */}
        {recommendation && (
          <div className="flex items-start gap-2 rounded-lg bg-white/70 px-3 py-2 text-xs text-gray-600">
            <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            <span>
              {recommendation.reason}（{recommendation.aspect_note}）。
              作成後に①情報登録タブでいつでも変更できます。
            </span>
          </div>
        )}

        {/* タイトル（任意。空ならサーバー側で自動命名） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">タイトル（任意）</label>
          <TaskyInput
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
            maxLength={100}
            placeholder="未入力の場合は自動で名前が付きます"
          />
        </div>

        {/* 商品選択（任意）。選んだ画像はサムネイル表示され product_image_keys に反映される */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">商品選択（任意）</label>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <TaskyButton type="button" variant="outline" size="sm" onClick={() => setShowProductSelectionModal(true)}>
              <Package className="w-3.5 h-3.5" />
              {selectedProductData ? '商品を変更' : '商品を選択する'}
            </TaskyButton>
            {selectedProductData && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                {selectedProductData.product.name}
              </span>
            )}
          </div>
          {imageItems.length > 0 && (
            <UgcAdImageKeysField items={imageItems} onChange={setImageItems} disabled={isCreating} hideUpload />
          )}
        </div>

        <div className="flex justify-end pt-1">
          <TaskyButton
            type="button"
            onClick={handleCreate}
            isLoading={isCreating}
            disabled={isLoadingRecommendation}
          >
            <Sparkles className="w-4 h-4" />
            作成する
          </TaskyButton>
        </div>
      </div>

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}
    </TaskyCard>
  );
};

export default UgcAdCreatePanel;
